
import _ from 'lodash'
import { formatDate } from '@/plugins/filters'
import { applyOrderCfsCalculator } from '@/api/OfflineFormSchemas'
import { projectedCfsAtOrderTime, relationId as cfsRelationId } from '@/api/OrderCfsProjection'

export default {
  name: 'CreateOrder',
  props: {
    orderData: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      open: true,
      active: {},
      producer: null,
      fetchingHistorical: false,
      historicalOrders: [],
      historicalSearched: false
    }
  },
  computed: {
    page () {
      return this.$store.getters['sectionSchemas/findbyname']('orders')
    },
    schemas () {
      return this.page.schemas
    },
    currentProducer () {
      return this.$store.getters['contacts/find'](this.active.producer)
    },
    preventSubmit () {
      return this.isEmpty(this.active.run_date) ||
          this.isEmpty(this.active.run_time) ||
          this.isEmpty(this.active.diversionPoint) ||
          this.isEmpty(this.active.farmunit) ||
          this.isEmpty(this.active.flow) ||
          this.isEmpty(this.active.type) ||
          this.cfsValidationErrors.length > 0
    },
    cfsValidationErrors () {
      const errors = []

      if (this.isEmpty(this.active.diversionPoint) || this.isEmpty(this.active.type)) {
        return errors
      }

      const originalCfs = this.projectedCfsForActiveOrderTime()
      const newCfs = Number(this.active.new_cfs)
      const netCfs = Number(this.active.net_cfs)

      if (!Number.isFinite(newCfs) || !Number.isFinite(netCfs)) {
        errors.push('invalid_cfs')
        return errors
      }

      if (originalCfs > 0 && this.active.type === 'On') {
        errors.push('on_when_running')
      }
      if (originalCfs <= 0 && this.active.type !== 'On') {
        errors.push('change_when_off')
      }
      if (this.active.type === 'On' && (newCfs <= 0 || netCfs <= 0)) {
        errors.push('invalid_on_cfs')
      }
      if (this.active.type === 'Off' && (newCfs !== 0 || netCfs !== -originalCfs)) {
        errors.push('invalid_off_cfs')
      }
      if (this.active.type === 'Increase' && (newCfs <= originalCfs || netCfs <= 0)) {
        errors.push('invalid_increase_cfs')
      }
      if (this.active.type === 'Decrease' && (newCfs >= originalCfs || newCfs < 0 || netCfs >= 0)) {
        errors.push('invalid_decrease_cfs')
      }

      return errors
    },
    formSchema () {
      const schema = _.cloneDeep(this.schemas.find(s => s.key === 'orders_form'))
      let headers = schema.headers

      headers = headers.map((header) => {
        if (header.field === 'producer' && header.options && header.options.propbindings) {
          header.options.propbindings.whereHasFallback = 'producesFu'
        }

      if (this.objHas(this.orderData, 'isLocked') && this.orderData.isLocked) {
          if (header.options.propbindings.isLockable) {
            header.type = 'textBlock'
            return header
          } else if (header.options.propbindings.showWhenUnlocked) {
            return null
          }
        } else if (header.options.propbindings.hideWhenUnlocked) {
          return null
        }

        return header
      })
        .filter(item => item)

      schema.headers = headers
      return applyOrderCfsCalculator(schema, this.active, {
        includeCalculator: !this.hasOrderTarget,
        hideContextFields: this.hasOrderTarget
      })
    },
    waterYear () {
      return this.$store.getters['system/getWaterYear']
    },
    canPrintTurnouts () {
      return !this.isEmpty(this.active.producer) && this.isEmpty(this.active.diversionPoint) && this.producerTurnouts.length > 0
    },
    producerTurnouts () {
      const producer = this.currentProducer || { producesDp: [] }
      const producesDp = Array.isArray(producer.producesDp) ? producer.producesDp : []
      const turnouts = this.$store.getters['diversionPoints/standardizedArray'](producesDp) || []

      return _.cloneDeep(turnouts).sort((a, b) => {
        const aUnit = this.sortableFarmunit(a)
        const bUnit = this.sortableFarmunit(b)

        if (aUnit !== bUnit) {
          return aUnit - bUnit
        }

        return String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { numeric: true })
      })
    },
    showHistoricalOrders () {
      return this.historicalSearched && !this.isEmpty(this.active.diversionPoint)
    },
    hasOrderTarget () {
      return !this.isEmpty(this.active.diversionPoint)
    }
  },
  watch: {
    'active.producer': {
      handler (n, o) {
        this.setActive()
        this.active.producer = n
        this.producer = this.currentProducer
        this.clearHistoricalOrders()
      },
      immediate: true
    },
    'active.type': {
      handler (n) {
        this.$set(this.active, 'type', n)
        this.$set(this.active, 'order_no', this.generateOrderNo())
      }
    }
  },
  mounted () {
    this.setActive()
  },
  methods: {
    generateOrderNo () {
      const orderNumbers = Object.values(this.$store.getters['orders/getAll'])
        .filter((o) => {
          if (!this.isEmpty(this.active.diversionPoint)) {
            return o.diversionPoint === this.active.diversionPoint
          }
          return true
        })
        .filter(o => !this.isEmpty(o.order_no))
        .map(o => Number(o.order_no.split('_').pop()))
        .filter(i => !isNaN(i))

      const relatedUsage = this.getRelatedUsage(
        typeof this.active.producer === 'object' ? this.active.producer?.id : this.active.producer,
        typeof this.active.diversionPoint === 'object' ? this.active.diversionPoint?.id : this.active.diversionPoint,
        typeof this.active.ditch === 'object' ? this.active.ditch?.id : this.active.ditch
      )

      let max = 1
      if (this.active.type !== 'On' && relatedUsage) {
        max = relatedUsage.order_no.split('_').pop()
      } else if (!this.isEmpty(orderNumbers)) {
        max = Math.max(...orderNumbers)
        if (this.active.type === 'On') {
          max = max + 1
        }
      }

      return `${
              `${this.waterYear}`.substring(2)}_${this.active.diversionPoint}_${max}${
              !this.isEmpty(this.active.type) && this.active.type !== 'On' ? `_${this.active.type}` : ''
          }`
    },
    getRelatedUsage (producer, diversionPoint, ditch) {
      const allUsages = this.$store.getters['usages/standardized']
      const filteredUsages = allUsages.filter((usage) => {
        return (typeof usage.producer === 'object' ? usage.producer.id === producer : usage.producer === producer) &&
                  usage.diversionPoint.id === diversionPoint &&
                  usage.ditch.id === ditch &&
                  (
                    this.isEmpty(usage.off_date) ||
                      new Date(`${usage.off_date}T${usage.off_time}`) > new Date()
                  )
      })
      return _.first(filteredUsages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))) || null
    },
    success () {
      this.$emit('closeOrderForm')
    },
    submitOrderForm () {
      this.$refs.orderForm?.submitForm()
    },
    submitOrderAndNew () {
      this.$refs.orderForm?.submitAndNew()
    },
    setActive () {
      this.active = {
        ...JSON.parse(JSON.stringify(this.$store.state.orders.bound)),
        ..._.cloneDeep(this.orderData)
      }
    },
    emitUp (emittedValue) {
      if (emittedValue.event) {
        this[emittedValue.event](emittedValue.value)
      }
    },
    showProducerTurnoutList () {},
    sortableFarmunit (turnout) {
      const unit = parseFloat(turnout?.farmunit?.unit_id)
      return Number.isNaN(unit) ? Number.MAX_SAFE_INTEGER : unit
    },
    printableText (value) {
      if (value === null || value === undefined || value === '') {
        return ''
      }
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;')
    },
    relationId (value) {
      return cfsRelationId(value)
    },
    turnoutOrders (turnout) {
      return Object.values(this.$store.getters['orders/getAll'] || {})
        .filter(order => String(this.relationId(order.diversionPoint)) === String(turnout.id))
    },
    scheduledOrderForTurnout (turnout) {
      return _.first(
        this.turnoutOrders(turnout)
          .filter(order => this.isIncomplete(order))
          .sort((a, b) => this.scheduledDate(a) - this.scheduledDate(b))
      )
    },
    runningOrderForTurnout (turnout) {
      const usages = Object.values(this.$store.getters['usages/getAll'] || {})
        .filter(usage => String(this.relationId(usage.diversionPoint)) === String(turnout.id))

      return _.first(
        this.currentlyRunning(this.$store.getters['usages/standardizedArray'](usages))
      )
    },
    currentCfsForActiveDiversionPoint () {
      if (this.isEmpty(this.active.diversionPoint)) {
        return 0
      }

      const diversionPointId = String(this.relationId(this.active.diversionPoint))
      const usages = Object.values(this.$store.getters['usages/getAll'] || {})
      const standardized = this.$store.getters['usages/standardizedArray'](usages)

      return this.currentlyRunning(
        standardized.filter(usage => String(this.relationId(usage.diversionPoint)) === diversionPointId)
      ).reduce((total, usage) => total + (Number(usage.flow) || 0), 0)
    },
    projectedCfsForActiveOrderTime () {
      return projectedCfsAtOrderTime({
        order: this.active,
        orders: Object.values(this.$store.getters['orders/getAll'] || {}),
        currentCfs: this.currentCfsForActiveDiversionPoint()
      })
    },
    isIncomplete (order) {
      return order.completed === false || order.completed === 0 || order.completed === '0' || order.completed === null || order.completed === undefined
    },
    scheduledDate (order) {
      return new Date(`${order.run_date} ${order.run_time || '00:00:00'}`)
    },
    turnoutStatusText (turnout) {
      const runningOrder = this.runningOrderForTurnout(turnout)
      if (runningOrder) {
        const until = runningOrder.off_date ? ` Until ${formatDate(runningOrder.off_date)} ${runningOrder.off_time || ''}` : ''
        return `Started running ${runningOrder.flow} CFS at ${formatDate(runningOrder.on_date)} ${runningOrder.on_time || ''}${until}`
      }

      const scheduledOrder = this.scheduledOrderForTurnout(turnout)
      if (scheduledOrder) {
        return `Scheduled ${scheduledOrder.flow} CFS ${scheduledOrder.type} order for ${formatDate(scheduledOrder.run_date)}`
      }

      return 'OFF'
    },
    printProducerTurnouts () {
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        this.showErrorSnack('Unable to open print window.')
        return
      }

      const producerName = this.printableText(this.currentProducer?.display_name || 'Selected Producer')
      const rows = this.producerTurnouts.map(turnout => `
        <tr>
          <td>${this.printableText(turnout?.farmunit?.unit_id)}</td>
          <td>${this.printableText(turnout?.division?.name)}</td>
          <td>${this.printableText(turnout?.ditch?.name)}</td>
          <td>${this.printableText(turnout?.name)}</td>
          <td>${this.printableText(turnout?.description)}</td>
          <td>${this.printableText(this.turnoutStatusText(turnout))}</td>
        </tr>
      `).join('')

      printWindow.document.write(`
        <!doctype html>
        <html>
          <head>
            <title>${producerName} Turnouts</title>
            <style>
              @page { margin: 0.5in; }
              body {
                color: #1f2933;
                font-family: Arial, Helvetica, sans-serif;
                font-size: 11px;
                margin: 0;
              }
              h1 {
                font-size: 18px;
                margin: 0 0 4px;
              }
              .subtitle {
                color: #52606d;
                margin-bottom: 16px;
              }
              table {
                border-collapse: collapse;
                width: 100%;
              }
              th,
              td {
                border-bottom: 1px solid #d9e2ec;
                padding: 7px 8px;
                text-align: left;
                vertical-align: top;
              }
              th {
                color: #334e68;
                font-size: 10px;
                text-transform: uppercase;
              }
              tr {
                break-inside: avoid;
                page-break-inside: avoid;
              }
            </style>
          </head>
          <body>
            <h1>${producerName}</h1>
            <div class="subtitle">Farm Units, Ditches, and Turnouts</div>
            <table>
              <thead>
                <tr>
                  <th>Farm Unit</th>
                  <th>Division</th>
                  <th>Ditch</th>
                  <th>T/O</th>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>${rows}</tbody>
            </table>
          </body>
        </html>
      `)
      printWindow.document.close()
      printWindow.focus()
      printWindow.print()
      printWindow.close()
    },
    savedAndNowNew () {
      this.active = {
        ...JSON.parse(JSON.stringify(this.$store.state.orders.bound)),
        ...{
          producer: this.active.producer,
          diversionPoint: this.active.diversionPoint,
          farmunit: this.active.farmunit
        }
      }

      this.open = true
    },
    dpSelected (dp) {
      this.$nextTick(() => {
        this.$set(this.active, 'diversionPoint', dp.id)
        this.$set(this.active, 'farmunit', dp.farmunit.id)
        this.$set(this.active, 'ditch', dp.ditch.id)
        this.$set(this.active, 'division', dp.division.id)
        this.$set(this.active, 'completed', false)
        this.$set(this.active, 'original_cfs', '')
        this.$set(this.active, 'new_cfs', '')
        this.$set(this.active, 'net_cfs', '')
        this.$set(this.active, 'cfs_entry_mode', '')
        this.$set(this.active, 'flow', '')
        this.$set(this.active, 'order_no', this.generateOrderNo())
        this.clearHistoricalOrders()
      })
    },
    setFlow (flow) {
      this.$nextTick(() => {
        this.$set(this.active, 'flow', flow)
      })
    },
    dpRemoved () {
      this.$nextTick(() => {
        this.$set(this.active, 'diversionPoint', '')
        this.$set(this.active, 'farmunit', '')
        this.$set(this.active, 'type', '')
        this.$set(this.active, 'flow', '')
        this.$set(this.active, 'original_cfs', '')
        this.$set(this.active, 'new_cfs', '')
        this.$set(this.active, 'net_cfs', '')
        this.$set(this.active, 'cfs_entry_mode', '')
        this.$set(this.active, 'ditch', '')
        this.$set(this.active, 'division', '')
        this.$set(this.active, 'order_no', '')
        this.clearHistoricalOrders()
      })
    },
    setType (type) {
      this.$nextTick(() => {
        this.$set(this.active, 'type', type)
        this.$set(this.active, 'order_no', this.generateOrderNo())
      })
    },
    clearHistoricalOrders () {
      this.historicalOrders = []
      this.historicalSearched = false
    },
    async fetchHistoricalOrders() {
      this.fetchingHistorical = true
      this.historicalSearched = true
      try {
        const response = await this.$repositories.historicalOrders.index({
          diversionPoint: this.active.diversionPoint,
          waterYear: this.$store.state.system.waterYear
        })

        if (this.objHas(response, 'error')) {
          this.showErrorSnack('Failed to load historical orders.')
          this.historicalOrders = []
        } else {
          this.historicalOrders = Object.values(response.data || {})
            .sort((a, b) => new Date(this.historicalSortDate(b)) - new Date(this.historicalSortDate(a)))
        }
      } catch {
        this.showErrorSnack('Failed to load historical orders.')
        this.historicalOrders = []
      } finally {
        this.fetchingHistorical = false
      }
    },
    historicalSortDate (order) {
      return order.run_date || order.on_date || order.created_at || ''
    },
    historicalStart (order) {
      return this.formatHistoricalDateTime(order.run_date || order.on_date, order.run_time || order.on_time)
    },
    historicalEnd (order) {
      return this.formatHistoricalDateTime(order.off_date, order.off_time)
    },
    formatHistoricalDateTime (date, time) {
      if (!date) {
        return '--'
      }
      return `${formatDate(date)}${time ? ` ${time}` : ''}`
    },
    relationLabel (value, key) {
      if (value && typeof value === 'object') {
        const displayFallback = key === 'display_name' ? (value.full_name || value.company) : null
        return value[key] || displayFallback || value.name || value.id || '--'
      }
      return value || '--'
    },
    formatCfs (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(2) : '--'
    }

  }
}
